// @ts-nocheck
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { userHasAnyRole, withAuthoritativeRole } from '@/lib/userRoles';
import { useNavigate } from 'react-router-dom';

interface ImpersonationContextType {
  originalUser: User | null;
  impersonatedUser: User | null;
  impersonatedProfile: any | null;
  isImpersonating: boolean;
  startImpersonation: (profileId: string) => Promise<void>;
  stopImpersonation: () => void;
}

const AdminImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const AdminImpersonationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user: currentUser, profile: currentProfile } = useAuth();
  const navigate = useNavigate();
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [impersonatedProfile, setImpersonatedProfile] = useState<any | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Check for existing impersonation state on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('admin_impersonation_state');
    if (savedState) {
      try {
        const { originalUserId, impersonatedProfileId } = JSON.parse(savedState);
        if (currentUser && currentUser.id === originalUserId && impersonatedProfileId) {
          // Restore impersonation state using profile ID
          restoreImpersonation(impersonatedProfileId);
        } else {
          // Clear invalid state
          sessionStorage.removeItem('admin_impersonation_state');
        }
      } catch (error) {
        console.error('🔍 Admin impersonation - Error restoring impersonation state:', error);
        sessionStorage.removeItem('admin_impersonation_state');
      }
    }
  }, [currentUser]);

  const restoreImpersonation = async (impersonatedProfileId: string) => {
    try {
      console.log('🔍 Admin impersonation - Restoring impersonation for profile ID:', impersonatedProfileId);

      let profileData: any = null;
      try {
        const { data, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', impersonatedProfileId)
          .single();
        if (profileError) {
          console.error('🔍 Admin impersonation - Profile restore error:', profileError);
        } else {
          profileData = data;
        }
      } catch (innerErr) {
        console.error('🔍 Admin impersonation - Profile restore threw:', innerErr);
      }

      if (!profileData) {
        sessionStorage.removeItem('admin_impersonation_state');
        return;
      }

      // Normalize the impersonated role the same way login does, so an
      // impersonated session can never be more privileged than user_roles says.
      const normalizedProfile = await withAuthoritativeRole(profileData, profileData.user_id);

      setOriginalUser(currentUser);
      setImpersonatedUser(currentUser);
      setImpersonatedProfile(normalizedProfile);
      setIsImpersonating(true);

      localStorage.setItem('activeProfileId', profileData.id);
      localStorage.setItem('actingAs', 'admin');
    } catch (error) {
      console.error('🔍 Admin impersonation - Error restoring impersonation:', error);
      sessionStorage.removeItem('admin_impersonation_state');
      setIsImpersonating(false);
      setImpersonatedProfile(null);
      setImpersonatedUser(null);
      setOriginalUser(null);
    }
  };

  const startImpersonation = async (profileId: string) => {
    if (!currentUser || !currentProfile) {
      toast.error('You must be logged in to impersonate users');
      return;
    }

    // Only real admins (per user_roles) may impersonate.
    const isAdmin = await userHasAnyRole(currentUser.id, ['admin']);
    if (!isAdmin) {
      toast.error('Only administrators can impersonate users');
      return;
    }

    try {
      console.log('🔍 Admin impersonation - Starting impersonation for profile ID:', profileId);
      
      // Fetch the complete athlete profile data by profile ID
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', profileId)
        .single();

      if (profileError) {
        console.error('🔍 Admin impersonation - Profile fetch error:', profileError);
        throw new Error(`Failed to fetch profile: ${profileError.message}`);
      }

      if (!profileData) {
        throw new Error('Profile not found');
      }

      console.log('🔍 Admin impersonation - Fetched profile data:', {
        id: profileData.id,
        name: profileData.name,
        email: profileData.email,
        role: profileData.role
      });

      // Set impersonation state with the fetched profile data
      setOriginalUser(currentUser);
      setImpersonatedUser(currentUser); // Keep admin's session for API calls
      setImpersonatedProfile(await withAuthoritativeRole(profileData, profileData.user_id));
      setIsImpersonating(true);

      // Set localStorage items for data fetching hooks
      localStorage.setItem('activeProfileId', profileData.id);
      localStorage.setItem('actingAs', 'admin');
      
      console.log('🔍 Admin impersonation - Set localStorage activeProfileId:', profileData.id);
      console.log('🔍 Admin impersonation - Set localStorage actingAs: admin');

      // Save state to sessionStorage for persistence
      sessionStorage.setItem('admin_impersonation_state', JSON.stringify({
        originalUserId: currentUser.id,
        impersonatedProfileId: profileId // Store profile ID, not user ID
      }));

      toast.success(`Now viewing as ${profileData.name || profileData.email}`);
      
      // Navigate to the athlete profile with edit mode enabled
      console.log('🔍 Admin impersonation - Navigating to: /athlete-dashboard/profile?edit=true');
      navigate('/athlete-dashboard/profile?edit=true');
    } catch (error) {
      console.error('🔍 Admin impersonation - Error starting impersonation:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start impersonation');
      
      // Clean up any partial state
      setIsImpersonating(false);
      setImpersonatedProfile(null);
      setImpersonatedUser(null);
      setOriginalUser(null);
    }
  };

  const stopImpersonation = () => {
    setOriginalUser(null);
    setImpersonatedUser(null);
    setImpersonatedProfile(null);
    setIsImpersonating(false);
    
    // Clean up localStorage items
    localStorage.removeItem('activeProfileId');
    localStorage.removeItem('actingAs');
    sessionStorage.removeItem('admin_impersonation_state');
    toast.success('Switched back to admin view');
    
    // Navigate back to admin dashboard
    navigate('/admin-dashboard');
  };

  // One identity while nothing changes — a new object here re-renders every
  // page that reads impersonation state.
  const value = React.useMemo(
    () => ({
      originalUser,
      impersonatedUser,
      impersonatedProfile,
      isImpersonating,
      startImpersonation,
      stopImpersonation,
    }),
    [originalUser, impersonatedUser, impersonatedProfile, isImpersonating]
  );

  return (
    <AdminImpersonationContext.Provider value={value}>
      {children}
    </AdminImpersonationContext.Provider>
  );
};

export const useAdminImpersonation = () => {
  const context = useContext(AdminImpersonationContext);
  if (context === undefined) {
    throw new Error('useAdminImpersonation must be used within AdminImpersonationProvider');
  }
  return context;
};